import { FunctionComponent, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { faCircleInfo, faTrash } from "@fortawesome/pro-regular-svg-icons";

import {
	useGetAvailableGroupsQuery,
	useGetPreferencesQuery,
	useUpsertPreferenceMutation,
	useDeletePreferenceMutation,
} from "@/pages/Private/redux/notification/notificationPreference.api";

import { Icon } from "@/components/Icon/Icon";
import { Toggle } from "@/components/Toggle/Toggle";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import {
	NotificationFrequency,
	NotificationPreferenceGroup,
} from "@/pages/Private/pages/Notifications/enum/notification.enum";
import { NotificationPreference } from "@/pages/Private/pages/Notifications/schema/notification";

const DEFAULT_PREF: Pick<
	NotificationPreference,
	"appEnabled" | "emailEnabled" | "digestEnabled" | "frequency"
> = {
	appEnabled: true,
	emailEnabled: true,
	digestEnabled: false,
	frequency: NotificationFrequency.INSTANT,
};

const FREQUENCY_LABEL_KEYS: Record<NotificationFrequency, string> = {
	[NotificationFrequency.INSTANT]: "instant",
	[NotificationFrequency.DAILY]: "daily",
	[NotificationFrequency.WEEKLY]: "weekly",
	[NotificationFrequency.OFF]: "off",
};

export const NotificationPreferences: FunctionComponent = () => {
	const { t } = useTranslation();
	const tp = (key: string) => t(`notifications.preferences.${key}`);

	const { data: groups, isLoading: isLoadingGroups } = useGetAvailableGroupsQuery(undefined);
	const { data: preferences, isLoading: isLoadingPrefs } = useGetPreferencesQuery(undefined);
	const [upsertPreference] = useUpsertPreferenceMutation();
	const [deletePreference] = useDeletePreferenceMutation();

	const prefMap = useMemo(() => {
		const map: Record<string, NotificationPreference> = {};

		if (preferences) {
			for (const p of preferences) {
				map[p.eventType] = p;
			}
		}

		return map;
	}, [preferences]);

	const handleToggle = useCallback(
		async (
			groupKey: NotificationPreferenceGroup,
			field: "appEnabled" | "emailEnabled" | "digestEnabled",
			currentValue: boolean
		) => {
			try {
				const existing = prefMap[groupKey];

				await upsertPreference({
					groupKey,
					appEnabled:
						field === "appEnabled"
							? !currentValue
							: existing?.appEnabled ?? DEFAULT_PREF.appEnabled,
					emailEnabled:
						field === "emailEnabled"
							? !currentValue
							: existing?.emailEnabled ?? DEFAULT_PREF.emailEnabled,
					digestEnabled:
						field === "digestEnabled"
							? !currentValue
							: existing?.digestEnabled ?? DEFAULT_PREF.digestEnabled,
					frequency: existing?.frequency ?? DEFAULT_PREF.frequency,
				});
			} catch (e) {
				console.error("Failed to update preference:", e);
			}
		},
		[prefMap, upsertPreference]
	);

	const handleFrequencyChange = useCallback(
		async (groupKey: NotificationPreferenceGroup, frequency: NotificationFrequency) => {
			try {
				const existing = prefMap[groupKey];

				await upsertPreference({
					groupKey,
					appEnabled: existing?.appEnabled ?? DEFAULT_PREF.appEnabled,
					emailEnabled: existing?.emailEnabled ?? DEFAULT_PREF.emailEnabled,
					digestEnabled: existing?.digestEnabled ?? DEFAULT_PREF.digestEnabled,
					frequency,
				});
			} catch (e) {
				console.error("Failed to update frequency:", e);
			}
		},
		[prefMap, upsertPreference]
	);

	const handleResetToDefault = useCallback(
		async (groupKey: string) => {
			try {
				await deletePreference(groupKey);
			} catch (e) {
				console.error("Failed to reset preference:", e);
			}
		},
		[deletePreference]
	);

	const isLoading = isLoadingGroups || isLoadingPrefs;

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-8">
				<span className="text-sm text-gray-700">{tp("loading")}</span>
			</div>
		);
	}

	return (
		<div className="mb-2 bg-brand-white rounded-xl border border-[#EAECF0] bg-[#F7F8FB] p-6 max-w-[640px] ml-[32px]">
			<h3 className="text-base font-semibold text-gray-900 mb-1">{tp("title")}</h3>
			<p className="text-xs text-gray-700 mb-4">{tp("subtitle")}</p>

			<div className="grid grid-cols-[1fr_80px_80px_80px_120px_40px] gap-2 px-3 py-2 bg-white border border-b-transparent border-[#EAECF0] rounded-t-lg text-xs font-semibold text-text-meta uppercase">
				<span>{tp("headers.group")}</span>
				<span className="text-center">{tp("headers.inApp")}</span>
				<span className="text-center">{tp("headers.email")}</span>
				<span className="flex items-center justify-center gap-1">
					{tp("headers.digest")}
					<Tooltip content={<span className="normal-case font-normal">{tp("headers.digestTooltip")}</span>}>
						<Icon
							icon={faCircleInfo}
							size="sm"
							className="text-gray-500 hover:text-gray-700 cursor-help"
						/>
					</Tooltip>
				</span>
				<span className="text-center">{tp("headers.frequency")}</span>
				<span />
			</div>

			{/* Group rows */}
			<div className="border border-[#EAECF0] rounded-b-lg bg-white">
				{groups?.map((group) => {
					const pref = prefMap[group.key];
					const appEnabled = pref?.appEnabled ?? DEFAULT_PREF.appEnabled;
					const emailEnabled = pref?.emailEnabled ?? DEFAULT_PREF.emailEnabled;
					const digestEnabled = pref?.digestEnabled ?? DEFAULT_PREF.digestEnabled;
					const frequency = pref?.frequency ?? DEFAULT_PREF.frequency;

					return (
						<div
							key={group.key}
							className="grid grid-cols-[1fr_80px_80px_80px_120px_40px] gap-2 items-center px-3 py-2.5 border-b border-[#EAECF0] last:border-b-0"
						>
							<div>
								<span className="text-sm text-gray-900">{group.label}</span>
								<p className="text-xs text-gray-700 mt-0.5">{group.description}</p>
							</div>

							<div className="flex justify-center">
								<Toggle
									isChecked={appEnabled}
									onChange={() => handleToggle(group.key, "appEnabled", appEnabled)}
								/>
							</div>

							<div className="flex justify-center">
								<Toggle
									isChecked={emailEnabled}
									onChange={() => handleToggle(group.key, "emailEnabled", emailEnabled)}
								/>
							</div>

							<div className="flex justify-center">
								<Toggle
									isChecked={digestEnabled}
									onChange={() => handleToggle(group.key, "digestEnabled", digestEnabled)}
								/>
							</div>

							<div className="flex justify-center">
								<select
									className="text-xs border border-[#EAECF0] rounded-lg px-2 py-1 bg-white text-gray-700 cursor-pointer"
									value={frequency}
									onChange={(e) =>
										handleFrequencyChange(group.key, e.target.value as NotificationFrequency)
									}
								>
									{Object.values(NotificationFrequency).map((freq) => (
										<option key={freq} value={freq}>
											{tp(`frequencyOptions.${FREQUENCY_LABEL_KEYS[freq]}`)}
										</option>
									))}
								</select>
							</div>

							<div className="flex justify-center">
								{pref && (
									<Tooltip content={<>{tp("resetToDefaults")}</>}>
										<button
											className="text-gray-700 hover:text-status-error cursor-pointer transition-colors"
											onClick={() => handleResetToDefault(group.key)}
										>
											<Icon icon={faTrash} size="sm" />
										</button>
									</Tooltip>
								)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};
